/** The player's high scores and Daily Run bests, kept in localStorage (docs/spec/04 §6). */

import type { ScoreEntry, TableId, Tables } from '../game/hiscores';
import { insertScore, parseTables, rankFor } from '../game/hiscores';
import type { KeyValueStore } from './storage';
import { KEYS } from './storage';

/** A day's best in the Daily Run. */
export interface DailyBest {
  readonly score: number;
  readonly stage: number;
  readonly attempts: number;
}

export class ScoreBook {
  private tables: Tables;
  private daily: Record<string, DailyBest>;

  constructor(private readonly store: KeyValueStore) {
    this.tables = parseTables(store.load(KEYS.scores));
    this.daily = parseDaily(store.load(KEYS.daily));
  }

  table(id: TableId): readonly ScoreEntry[] {
    return this.tables[id];
  }

  /** The score to beat, shown as HIGH SCORE. */
  best(id: TableId): number {
    return this.tables[id][0]?.score ?? 0;
  }

  /** Where `score` would land in the table (0-based), or -1. */
  rank(id: TableId, score: number): number {
    return rankFor(this.tables[id], score);
  }

  /** Add a score; the rank it took, or -1 if it did not make the table. */
  add(id: TableId, entry: ScoreEntry): number {
    const { tables, rank } = insertScore(this.tables, id, entry);
    if (rank >= 0) {
      this.tables = tables;
      this.store.save(KEYS.scores, JSON.stringify(tables));
    }
    return rank;
  }

  /** The best of the day `date` (YYYY-MM-DD), or null if it has not been played. */
  dailyBest(date: string): DailyBest | null {
    return this.daily[date] ?? null;
  }

  /** Record a finished Daily Run; true if it is the day's new best. */
  recordDaily(date: string, score: number, stage: number): boolean {
    const before = this.daily[date];
    const better = !before || score > before.score;
    this.daily = {
      // Only the last fortnight is worth keeping.
      ...Object.fromEntries(Object.entries(this.daily).sort().slice(-13)),
      [date]: {
        score: better ? score : before.score,
        stage: better ? stage : before.stage,
        attempts: (before?.attempts ?? 0) + 1,
      },
    };
    this.store.save(KEYS.daily, JSON.stringify(this.daily));
    return better;
  }
}

function parseDaily(json: string | null): Record<string, DailyBest> {
  if (!json) return {};
  try {
    const raw = JSON.parse(json) as Record<string, unknown>;
    const out: Record<string, DailyBest> = {};
    for (const [date, v] of Object.entries(raw)) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || typeof v !== 'object' || v === null) continue;
      const e = v as Partial<DailyBest>;
      if (typeof e.score !== 'number') continue;
      out[date] = {
        score: Math.max(0, Math.floor(e.score)),
        stage: Math.max(1, Math.floor(Number(e.stage) || 1)),
        attempts: Math.max(1, Math.floor(Number(e.attempts) || 1)),
      };
    }
    return out;
  } catch {
    return {};
  }
}
